import { FC, PointerEvent as PointerEventReact, RefObject } from "react";
import { ITrialCalendarSlot, ITrialLesson } from "../../../../entities/trialLesson";
import { CALENDAR_DAY_SLOT_HEIGHT_PX } from "../../../../shared/lib/const/calendarDay";
import { formatLessonsCount } from "../../../../shared/lib/helpers/formatRuCount";
import { useCalendarDayEventsLayer } from "../../../../shared/lib/hooks/useCalendarDayEventsLayer";
import {
    IDisplayedPositionedTrialLesson,
    IGroupedTrialLessons,
} from "../../lib/types";
import { TrialCalendarDayLessonBlock } from "../lessonBlock/TrialCalendarDayLessonBlock";
import { TrialLessonsPopover } from "../lessonsPopover/TrialLessonsPopover";
import { getMoscowDateTimeParts } from "../../../../shared/lib/helpers/moscowDateTime";
import classes from '../trialCalendarDay.module.scss'

interface IActiveSelection {
    startIndex: number;
    endIndex: number;
}

interface IProps {
    slotsRef: RefObject<HTMLUListElement | null>;
    slots: Date[];
    calendarSlotsByIndex: Map<number, ITrialCalendarSlot>;
    creationMode: boolean;
    activeSelection: IActiveSelection | null;
    visibleLessons: IDisplayedPositionedTrialLesson[];
    groupedLessons: IGroupedTrialLessons[];
    compactTime: boolean;
    formatTime: (date: Date) => string;
    getTrialLessonTimeRange: (trialLesson: ITrialLesson) => string;
    getTrialLessonStatusClass: (status: ITrialLesson['status']) => string;
    onPointerDown: (event: PointerEventReact<HTMLLIElement>, slotIndex: number) => void;
    onPointerMove: (event: PointerEventReact<HTMLUListElement>) => void;
    onPointerUp: (event: PointerEventReact<HTMLUListElement>) => void;
    onPointerCancel: () => void;
    onSelectTrialLesson: (trialLesson: ITrialLesson) => void;
}

export const TrialCalendarDaySlots: FC<IProps> = ({
    slotsRef, slots, calendarSlotsByIndex, creationMode, activeSelection, visibleLessons, groupedLessons,
    compactTime,
    formatTime, getTrialLessonTimeRange, getTrialLessonStatusClass, onPointerDown, onPointerMove,
    onPointerUp, onPointerCancel, onSelectTrialLesson,
}) => {
    const {eventsLayerRef, eventsLayerWidth, getEventPosition} =
        useCalendarDayEventsLayer(formatLessonsCount)

    return (
        <ul
            ref={slotsRef}
            className={classes.slots}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
        >
            {slots.map((slotDate, slotIndex) => {
                const calendarSlot = calendarSlotsByIndex.get(slotIndex)
                const isAvailable = calendarSlot?.availabilityStatus === 'TRIAL_SLOT_AVAILABILITY_STATUS_AVAILABLE'
                const isUnavailable = calendarSlot?.availabilityStatus === 'TRIAL_SLOT_AVAILABILITY_STATUS_UNAVAILABLE'
                const canCreateTrial = creationMode && isAvailable && Boolean(
                    calendarSlot?.availableActions.includes('CALENDAR_SLOT_ACTION_CREATE_TRIAL_LESSON')
                )
                const availabilityTitle = (() => {
                    if(isAvailable) return 'Свободный слот для пробного занятия'
                    if(calendarSlot?.availabilityStatus === 'TRIAL_SLOT_AVAILABILITY_STATUS_NOT_CALCULATED'){
                        return 'Доступность не рассчитана: выберите Telegram, тег, пол и исполнителя'
                    }
                    switch(calendarSlot?.unavailabilityReason){
                        case 'TRIAL_SLOT_UNAVAILABILITY_REASON_PAST':
                            return 'Недоступно: время уже прошло'
                        case 'TRIAL_SLOT_UNAVAILABILITY_REASON_TUTOR_FILTER_MISMATCH':
                            return 'Недоступно: исполнитель не соответствует выбранным фильтрам'
                        case 'TRIAL_SLOT_UNAVAILABILITY_REASON_OUTSIDE_SCHEDULE':
                            return 'Недоступно: слот находится вне рабочего времени исполнителя'
                        case 'TRIAL_SLOT_UNAVAILABILITY_REASON_REGULAR_LESSON':
                            return 'Недоступно: у исполнителя назначено регулярное занятие'
                        case 'TRIAL_SLOT_UNAVAILABILITY_REASON_TRIAL_LESSON':
                            return 'Недоступно: у исполнителя назначено пробное занятие'
                        default:
                            return isUnavailable ? 'Недоступный слот' : 'Доступность слота не определена'
                    }
                })()
                const isSelected = activeSelection &&
                    slotIndex >= activeSelection.startIndex &&
                    slotIndex <= activeSelection.endIndex

                return (
                    <li
                        data-slot-index={slotIndex}
                        key={slotDate.getTime()}
                        title={availabilityTitle}
                        className={classes.slot +
                            (isAvailable ? ` ${classes.available}` : '') +
                            (isUnavailable ? ` ${classes.unavailable}` : '') +
                            (canCreateTrial ? ` ${classes.selectable}` : '') +
                            (isSelected ? ` ${classes.selected}` : '')
                        }
                        onPointerDown={event => onPointerDown(event, slotIndex)}
                        onDrag={event => event.preventDefault()}
                        onDragStart={event => event.preventDefault()}
                    >
                        {getMoscowDateTimeParts(slotDate).minutes === 0 && (
                            <span className={classes.hour}>{formatTime(slotDate)}</span>
                        )}
                    </li>
                )
            })}
            <li className={classes.slotHour}>
                <span className={classes.hour}>{compactTime ? '24' : '24:00'}</span>
            </li>
            <li ref={eventsLayerRef} className={classes.eventsLayer}>
                {visibleLessons.map(positionedLesson => {
                    const position = getEventPosition(positionedLesson.columnsLayout, false)

                    return (
                        <TrialCalendarDayLessonBlock
                            key={positionedLesson.trialLesson.id}
                            trialLesson={positionedLesson.trialLesson}
                            startIndex={positionedLesson.startIndex}
                            endIndex={positionedLesson.endIndex}
                            left={position.left}
                            width={position.width}
                            zIndex={position.zIndex}
                            visibleStartsAt={positionedLesson.visibleStartsAt}
                            visibleEndsAt={positionedLesson.visibleEndsAt}
                            formatTime={formatTime}
                            statusClassName={getTrialLessonStatusClass(positionedLesson.trialLesson.status)}
                            onSelect={onSelectTrialLesson}
                        />
                    )
                })}
                {groupedLessons.map(group => {
                    const position = getEventPosition(group.columnsLayout, true)

                    return (
                        <TrialLessonsPopover
                            key={`${group.status}-${group.startIndex}-${group.lane}`}
                            trialLessons={group.events.map(event => event.trialLesson)}
                            top={group.startIndex * CALENDAR_DAY_SLOT_HEIGHT_PX}
                            left={position.left}
                            width={position.width}
                            height={Math.max(
                                group.durationSlots * CALENDAR_DAY_SLOT_HEIGHT_PX - 2,
                                CALENDAR_DAY_SLOT_HEIGHT_PX - 2
                            )}
                            zIndex={position.zIndex}
                            openToLeft={position.left + position.width + 320 > eventsLayerWidth}
                            getTrialLessonTimeRange={getTrialLessonTimeRange}
                            getTrialLessonStatusClass={getTrialLessonStatusClass}
                            statusClassName={getTrialLessonStatusClass(group.status)}
                            onSelectTrialLesson={onSelectTrialLesson}
                        />
                    )
                })}
            </li>
        </ul>
    )
}
